import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { PlayFab } from './services/PlayFab';
import { Account } from './services/Account';
import { format } from 'date-fns';
import './HandHistoryPage.css';
import { AnalysisDisplay } from './components/AnalysisDisplay';
import { HandAnalysisData } from './types';

// Helper function (or import)
const getRatingCategory = (rating: number): string => {
  if (rating >= 9) return 'excellent';
  if (rating >= 7) return 'good';
  if (rating >= 4) return 'okay';
  if (rating >= 1) return 'poor';
  return 'unknown';
};

interface HandAnalysisPageState {
  selectedHand: HandAnalysisData | null;
  loading: boolean;
  error: string | null;
  shareSuccess: string | null;
  sessionIdForBackNav: string | null; // To store sessionId for back button
}

const HandAnalysisPage: React.FC = () => {
  const { playfabId, handId } = useParams<{ playfabId: string, handId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<HandAnalysisPageState>({
    selectedHand: null,
    loading: true,
    error: null,
    shareSuccess: null,
    sessionIdForBackNav: null,
  });

  useEffect(() => {
    const loadSelectedHandAnalysis = async (ownerId: string, targetHandId: string) => {
      setState(prevState => ({ ...prevState, loading: true, error: null }));
      console.log(`HandAnalysisPage: Loading analysis for hand ${targetHandId}, user ${ownerId}...`);

      try {
        const result = await PlayFab.executeCloudScript('getDynamoDBData', {
          action: 'getHand',
          handId: targetHandId,
          ownerPlayfabId: ownerId
        });

        console.log(`HandAnalysisPage: CloudScript response for hand ${targetHandId}, user ${ownerId}:`, result);

        const nestedResult = result.data?.FunctionResult?.FunctionResult;

        if (result.success && nestedResult?.success) {
          const handAnalysis: HandAnalysisData = nestedResult.data;
          if (handAnalysis && typeof handAnalysis === 'object' && handAnalysis.id) {
            console.log(`HandAnalysisPage: Successfully parsed analysis for hand ${targetHandId}.`);
            let sessionId: string | null = null;
            if (handAnalysis.SK && typeof handAnalysis.SK === 'string') {
                const skParts = handAnalysis.SK.split('#');
                if (skParts.length >= 2 && skParts[0] === 'SES') {
                    sessionId = skParts[1];
                }
            }
            setState(prevState => ({
              ...prevState,
              selectedHand: handAnalysis,
              sessionIdForBackNav: sessionId,
              loading: false,
            }));
          } else {
             console.error('HandAnalysisPage: Invalid data format received for hand analysis:', handAnalysis);
            throw new Error('Invalid data format received for hand analysis.');
          }
        } else {
          const errorMessage = nestedResult?.error || result.data?.FunctionResult?.error || 'Unknown error fetching hand analysis';
          console.error(`HandAnalysisPage: Error fetching analysis for hand ${targetHandId}, user ${ownerId}:`, errorMessage, nestedResult?.details);
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error(`HandAnalysisPage: Error loading hand analysis for hand ${targetHandId}, user ${ownerId}:`, error);
        setState(prevState => ({
          ...prevState,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load hand analysis',
          selectedHand: null,
          sessionIdForBackNav: null,
        }));
      }
    };

    if (playfabId && handId && typeof handId === 'string') {
      loadSelectedHandAnalysis(playfabId, handId);
    } else {
        console.error(`HandAnalysisPage: Invalid parameters received. playfabId: ${playfabId}, handId: ${handId} (type: ${typeof handId})`);
        setState(prevState => ({ ...prevState, loading: false, error: 'Hand ID missing or invalid in URL' }));
    }

  }, [playfabId, handId]); // Reload if either ID changes

  const handleShare = async () => {
    try {
      const shareUrl = window.location.href; // Get current browser URL
      await navigator.clipboard.writeText(shareUrl);
      setState(prevState => ({ ...prevState, shareSuccess: 'Current page link copied to clipboard!', error: null }));

      setTimeout(() => {
         setState(prevState => ({ ...prevState, shareSuccess: null }));
      }, 3000);

    } catch (error) {
      console.error('Error copying URL:', error);
       setState(prevState => ({
         ...prevState,
         error: error instanceof Error ? error.message : 'Failed to copy link',
         shareSuccess: null
       }));
    }
  };

  const handleGoBack = () => {
      if (playfabId && state.sessionIdForBackNav) {
          navigate(`/history/user/${playfabId}/sessions/${state.sessionIdForBackNav}`);
      } else if (playfabId) {
          navigate(`/history/user/${playfabId}/sessions`);
      } else {
          navigate('/history/sessions');
      }
  };

  const { selectedHand, loading, error, shareSuccess } = state;

  return (
    <div className="hand-history"> {/* Reuse container style */}
      <div className="hand-analysis-container" style={{ maxWidth: '900px', margin: '0 auto' }}> {/* Centered content */}
        {/* Back Button */}
         <button
            className="hand-history__back-button"
            onClick={handleGoBack}
            style={{ display: 'inline-block', marginBottom: '1rem' }}
          >
              &larr; Back to Hand List
          </button>

        {loading ? (
          <div className="hand-history__loading">Loading analysis...</div>
        ) : error ? (
          <div className="hand-history__error">{error}</div>
        ) : selectedHand ? (
          <>
            {/* Header with Share Button */}
            <div className="hand-history__header">
              <h2>
                Hand Analysis
                <span className="hand-history__timestamp">
                  {selectedHand.timestamp ? format(new Date(selectedHand.timestamp), 'PPp') : 'Date N/A'}
                </span>
                {/* Restore Share Hand Button */}
                <button
                  className="hand-history__share"
                  onClick={handleShare}
                >
                  Copy Share Link
                </button>
              </h2>
            </div>

            {/* Restore Share Success Message */}
            {shareSuccess && (
              <div className="hand-history__share-success">
                {shareSuccess}
              </div>
            )}

            {/* Analysis Display */}
            <AnalysisDisplay
              analysis={selectedHand}
              improvementFeedback="" // Add state/props for improvement later if needed
              isImprovingAnalysis={false}
              onImprovementFeedbackChange={() => {}}
              onImproveAnalysisRequest={() => {}}
            />
          </>
        ) : (
          <div className="hand-history__empty">Hand analysis not found.</div>
        )}
      </div>
    </div>
  );
};

export default HandAnalysisPage; 